import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';

interface Quote {
  id: string;
  createdDate: string;
  referenceNumber: string;
  consultant_id: string;
  status: string;
}

interface Consultant {
  id: string;
  first_name: string;
  last_name: string;
}

interface QuotesTabProps {
  clientId: string;
  onCreateQuote?: (clientId: string) => void;
}

const API_URL = process.env.REACT_APP_API_URL || '';

const QuotesTab: React.FC<QuotesTabProps> = ({ clientId, onCreateQuote }) => {
  const { can } = usePermissions();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  
  // Fetch quotes and consultants when component mounts
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Fetch quotes
        const quotesResponse = await axios.get(`${API_URL}/api/quotes/client/${clientId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const quotesData = quotesResponse.data.data || quotesResponse.data || [];
        
        setQuotes(quotesData);

        // Fetch consultants
        try {
          const consultantsResponse = await axios.get(`${API_URL}/api/quotes/consultants`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              clientId,
            },
          });

          const consultantsData = consultantsResponse.data.data || consultantsResponse.data || [];

          const formattedConsultants = consultantsData
            .map((consultant: any) => ({
              id: consultant.id?.toString(),
              first_name: consultant.first_name || consultant.name || 'Unknown',
              last_name: consultant.last_name || consultant.surname || '',
            }))
            .filter((consultant: Consultant) => consultant.id);

          setConsultants(formattedConsultants);

        } catch (consultantsErr) {
          console.error('Error fetching consultants, using fallback:', consultantsErr);
          setConsultants([
            { id: '1', first_name: 'Alice', last_name: 'Smith' },
            { id: '2', first_name: 'Bob', last_name: 'Johnson' },
          ]);
        }
      } catch (err: any) {
        console.error('Error fetching quotes:', err);
        setError('Failed to load quotes');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId]);

  if (!can('quotes', 'read')) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">You do not have permission to view Quotes.</p>
      </div>
    );
  }

  // Function to get consultant full name by ID
  const getConsultantFullName = (consultantId: string | undefined | null): string => {
    if (!consultantId) return 'Not Assigned';

    const consultantIdStr = String(consultantId).trim();
    const consultant = consultants.find((c) => c.id === consultantIdStr);

    if (consultant) {
      return `${consultant.first_name} ${consultant.last_name}`.trim();
    }

    return `Consultant ${consultantIdStr}`;
  };

  // Function to handle the "Create Quote" button click
  const handleCreateQuote = () => {
    if (onCreateQuote) {
      onCreateQuote(clientId);
    }
  };

  // ✅ Split the quotes into active and declined
  const activeQuotes = quotes.filter(q => q.status?.toLowerCase() !== 'declined');
  const declinedQuotes = quotes.filter(q => q.status?.toLowerCase() === 'declined');

  // Helper to render a table for a given set of quotes
  const renderQuoteTable = (quotesToRender: Quote[], isDeclined = false) => (
    <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
      <table className="min-w-full bg-white divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tl-lg">
              Created Date
            </th>
            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reference Number
            </th>
            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Consultant
            </th>
            <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rounded-tr-lg">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {quotesToRender.map((quote) => (
            <tr key={quote.id} className="hover:bg-gray-50">
              <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                {new Date(quote.createdDate).toLocaleString()}
              </td>
              <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                <Link
                  to={`/quotes/${quote.id}`}
                  state={{
                    activeTab: 'quotes',
                    selectedQuoteId: quote.id,
                    quoteReference: quote.referenceNumber,
                    consultantName: getConsultantFullName(quote.consultant_id),
                    clientId: clientId
                  }}
                  className="text-blue-600 hover:underline"
                >
                  {quote.referenceNumber}
                </Link>
              </td>
              <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                {getConsultantFullName(quote.consultant_id)}
              </td>
              <td className="py-3 px-4 whitespace-nowrap text-sm">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  isDeclined ? 'bg-red-100 text-red-800' :
                  quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  quote.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {quote.status}
                </span>
              </td>
            </tr>
          ))}
          {quotesToRender.length === 0 && (
            <tr>
              <td colSpan={4} className="py-6 px-4 text-center text-gray-500 italic">
                No quotes found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Quotes for Client ID: {clientId}</h3>
        <button
          onClick={handleCreateQuote}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
        >
          Create Quote
        </button>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading quotes...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <>
          {/* ✅ ACTIVE QUOTES SECTION */}
          <div className="mb-10">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Active Quotes ({activeQuotes.length})
            </h3>
            {renderQuoteTable(activeQuotes, false)}
          </div>

          {/* ✅ DECLINED QUOTES SECTION */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Declined Quotes ({declinedQuotes.length})
            </h3>
            {declinedQuotes.length > 0 ? (
              renderQuoteTable(declinedQuotes, true)
            ) : (
              <p className="text-gray-500 italic text-sm">No declined quotes.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QuotesTab;